import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PersonData, PersonStatus } from "@/entities/people/types";
import { ArrowUpRight, Mail, Phone } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { getFullName } from "@/entities/people/helpers";
import { getEnumTKey } from "@/utils/strings";
import IconContainer from "@/components/ui/icon-container";
import { personStatusColors } from "@/components/layouts/People/PeopleTable";
import { StatusBadge } from "@/components/ui/status-badge";
import { IconButton } from "@/components/ui/icon-button";

interface PersonCardProps {
  person: PersonData;
  getReportsToPersonName: (personId: string | null | undefined) => string;
}

const PersonCard: React.FC<PersonCardProps> = ({
  person,
  getReportsToPersonName
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      key={person.id}
      className="group flex flex-col w-full h-full p-4 gap-2 bg-surface-widget-card hover:bg-surface-hover-soft rounded-xl border-[0.5px] border-border-border1 transition-colors duration-200"
    >
      <div className="flex flex-row justify-between">
        <Text variant="lgLabel">{getFullName(person)}</Text>
        <div>
          <IconButton
            size="md"
            className="group-hover:opacity-100 opacity-0"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/people/${person.id}`);
            }}
          >
            <ArrowUpRight />
          </IconButton>
        </div>
      </div>
      <div className="flex flex-row flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {person?.jobTitle && <Text variant="body">{person?.jobTitle}</Text>}
          {person?.status && person?.jobTitle && (
            <span className="text-border-border2">|</span>
          )}
        </div>
        {person?.status && (
          <StatusBadge
            statusColor={personStatusColors[person?.status as PersonStatus]}
            containerClassName="w-fit"
          >
            {t(
              getEnumTKey(
                "people",
                "statuses",
                PersonStatus,
                person?.status as number
              )
            )}
          </StatusBadge>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-2">
          <Text variant="bodySecondary">
            {t("people.personView.reportsTo")}
          </Text>
          <Text
            variant="body"
            className="hover:text-link cursor-pointer transition-colors duration-200"
            onClick={() => {
              // Reports to functionality handled by table
            }}
          >
            {getReportsToPersonName(person?.reportsTo)}
          </Text>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <IconContainer color="secondary">
            <Mail />
          </IconContainer>
          <Text
            variant="body"
            copyable
            copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
          >
            {person?.emails?.[0]}
          </Text>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <IconContainer color="secondary">
            <Phone />
          </IconContainer>
          <Text
            variant="body"
            copyable
            copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
          >
            {person?.phone}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PersonCard;
